import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Package, Truck, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { fetchOrder } from '@/lib/api';
import { useApp } from '@/state/AppContext';
import { inr } from '@/lib/format';
import { Footer } from '@/components/Footer';
import { toast } from 'sonner';

const STAGES = [
  { id: 'confirmed', label: 'Confirmed', Icon: CheckCircle2 },
  { id: 'packed', label: 'Packed', Icon: Package },
  { id: 'shipped', label: 'Shipped', Icon: Truck },
  { id: 'delivered', label: 'Delivered', Icon: CheckCircle2 },
];

const OrderTrackingPage = () => {
  const [params] = useSearchParams();
  const { cart } = useApp();
  const [orderId, setOrderId] = useState(params.get('order_id') || '');
  const [email, setEmail] = useState(params.get('email') || localStorage.getItem('bb_last_email') || '');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const lookup = async (oid = orderId, em = email) => {
    if (!oid || !em) { toast.error('Enter Order ID and email'); return; }
    setLoading(true);
    setError('');
    try {
      const o = await fetchOrder(oid, { email: em });
      setOrder(o);
    } catch (e) {
      setOrder(null);
      setError(e?.response?.data?.detail || 'Could not find that order.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.get('order_id') && (params.get('email') || localStorage.getItem('bb_last_email'))) {
      lookup(params.get('order_id'), params.get('email') || localStorage.getItem('bb_last_email'));
    }
    // eslint-disable-next-line
  }, []);

  const stageIdx = order ? STAGES.findIndex((s) => s.id === order.status) : -1;

  return (
    <div data-testid="profile-page">
      <section className="bb-cloud-bg">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-6">
          <h1 className="font-display text-3xl md:text-4xl font-bold">Track your order</h1>
          <p className="text-sm text-muted-foreground mt-1">Guest checkout means no account — just your Order ID + email.</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 md:px-6 mt-4">
        <div className="rounded-2xl bg-white border border-border p-4 bb-shadow-soft">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Order ID</Label>
              <Input value={orderId} onChange={(e) => setOrderId(e.target.value.toUpperCase())} placeholder="BB-XXXXXX" className="h-11 rounded-xl mt-1" data-testid="order-tracking-input" />
            </div>
            <div>
              <Label className="text-xs">Email used at checkout</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-11 rounded-xl mt-1" data-testid="order-tracking-email" />
            </div>
          </div>
          <Button onClick={() => lookup()} disabled={loading} className="mt-3 w-full h-12 rounded-xl bg-[var(--bb-pink)] hover:bg-[var(--bb-pink-deep)] text-white" data-testid="order-tracking-submit-button">
            <Search className="w-4 h-4 mr-1.5" /> {loading ? 'Looking up…' : 'Track Order'}
          </Button>
          {error && <div className="mt-3 text-sm text-[var(--bb-pink-deep)]">{error}</div>}
        </div>

        {order && (
          <div className="rounded-2xl bg-white border border-border p-4 mt-4 bb-shadow-soft" data-testid="tracking-result">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">Order ID</div>
                <div className="font-bold">{order.order_id}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Total</div>
                <div className="font-bold">{inr(order.total)}</div>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              {STAGES.map((s, i) => {
                const reached = i <= Math.max(0, stageIdx);
                const Icon = s.Icon;
                return (
                  <div key={s.id} className="flex-1 flex flex-col items-center">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center border ${reached ? 'bg-[var(--bb-pink)] text-white border-transparent' : 'bg-white text-muted-foreground border-border'}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className={`text-[11px] mt-1 ${reached ? 'font-semibold' : 'text-muted-foreground'}`}>{s.label}</div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 space-y-2">
              {order.items.map((it, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <img src={it.customization?.preview_data_url || it.image} alt={it.name} className="w-12 h-12 rounded-lg object-cover bg-[var(--bb-sky-soft)]" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{it.name}</div>
                    <div className="text-xs text-muted-foreground">Qty {it.qty}{it.customization ? ' · Custom' : ''}</div>
                  </div>
                  <div className="text-sm font-semibold">{inr(it.price * it.qty)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!order && cart.length > 0 && (
          <div className="text-center text-sm text-muted-foreground mt-6">
            Your active cart has {cart.length} item{cart.length > 1 ? 's' : ''}. <Link to="/cart" className="text-[var(--bb-pink-deep)] font-semibold">View cart →</Link>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
};

export default OrderTrackingPage;
